import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Truck, FileText, Car, Settings, ExternalLink, Home, Users, Calendar, MapPin, DollarSign, Package, Briefcase, Phone, Mail, Image as ImageIcon, Upload } from 'lucide-react';
import api from '../services/api';
import { API } from '../App';
import { toast } from 'sonner';

const DashboardLinkModal = ({ isOpen, onClose, onSave, editLink, user }) => {
  const [formData, setFormData] = useState({
    name: '',
    url: '',
    icon_type: 'FileText',
    icon_url: null,
    group_id: null,
    is_global: false,
  });
  const [uploadingIcon, setUploadingIcon] = useState(false);
  const [groups, setGroups] = useState([]);

  const enconOrange = '#ee7203';

  // Predefined icon options
  const iconOptions = [
    { name: 'Transport Manager', icon: Truck },
    { name: 'Reports', icon: FileText },
    { name: 'Company Car', icon: Car },
    { name: 'Settings', icon: Settings },
    { name: 'External Link', icon: ExternalLink },
    { name: 'Home', icon: Home },
    { name: 'Users', icon: Users },
    { name: 'Calendar', icon: Calendar },
    { name: 'Map', icon: MapPin },
    { name: 'Finance', icon: DollarSign },
    { name: 'Package', icon: Package },
    { name: 'Briefcase', icon: Briefcase },
    { name: 'Phone', icon: Phone },
    { name: 'Email', icon: Mail },
    { name: 'Staffology', icon: ExternalLink },
    { name: 'CRM', icon: ExternalLink },
  ];

  useEffect(() => {
    if (isOpen) {
      loadGroups();
      if (editLink) {
        setFormData({
          name: editLink.name,
          url: editLink.url,
          icon_type: editLink.icon_type || editLink.iconType,
          icon_url: editLink.icon_url,
          group_id: editLink.group_id,
          is_global: editLink.is_global || editLink.isGlobal,
          visible_to_all: editLink.visible_to_all !== undefined ? editLink.visible_to_all : true,
          is_external: editLink.is_external,
        });
      } else {
        // Reset form for new link
        setFormData({
          name: '',
          url: '',
          icon_type: 'FileText',
          icon_url: null,
          group_id: null,
          is_global: user?.role === 'super_admin',
          visible_to_all: true,
          is_external: true,
        });
      }
    }
  }, [isOpen, editLink, user]);

  const loadGroups = async () => {
    try {
      const response = await api.get(`${API}/dashboard-groups`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setGroups(response.data.groups || []);
    } catch (error) {
      console.error('Error loading groups:', error);
      setGroups([]);
    }
  };

  const handleIconUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('File size must be less than 2MB');
      return;
    }

    // Check file type
    if (!file.type.startsWith('image/')) {
      toast.error('File must be an image');
      return;
    }

    setUploadingIcon(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post(`${API}/upload-icon`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });

      setFormData(prev => ({
        ...prev,
        icon_url: response.data.url,
        icon_type: 'custom'
      }));
      toast.success('Icon uploaded successfully');
    } catch (error) {
      console.error('Error uploading icon:', error);
      toast.error('Failed to upload icon');
    } finally {
      setUploadingIcon(false);
    }
  };

  const handleSave = async () => {
    if (!formData.name || !formData.url) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      if (editLink) {
        // Update existing link
        await api.put(`${API}/dashboard-links/${editLink.id}`, formData, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        toast.success('Link updated successfully');
      } else {
        // Create new link
        await api.post(`${API}/dashboard-links`, formData, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        toast.success('Link added successfully');
      }
      onSave();
      onClose();
    } catch (error) {
      console.error('Error saving link:', error);
      toast.error(error.response?.data?.detail || 'Failed to save link');
    }
  };

  const renderIconPreview = () => {
    if (formData.icon_url) {
      return <img src={formData.icon_url} alt="Custom icon" className="w-12 h-12 object-contain" />;
    }
    const IconComponent = iconOptions.find(opt => opt.name === formData.icon_type)?.icon || FileText;
    return <IconComponent className="w-12 h-12" style={{ color: enconOrange }} />;
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl w-[95vw] md:w-[85vw] lg:max-w-3xl max-h-[80vh] overflow-y-auto bg-white">
        <DialogHeader>
          <DialogTitle style={{ color: '#1d1d1b' }}>
            {editLink ? 'Edit Link' : 'Add New Link'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 pt-4">
          {/* Icon Preview */}
          <div className="flex justify-center">
            <div
              className="w-20 h-20 flex items-center justify-center rounded-2xl"
              style={{
                backgroundColor: 'rgba(238, 114, 3, 0.1)',
                border: `2px solid ${enconOrange}`,
              }}
            >
              {renderIconPreview()}
            </div>
          </div>

          {/* Name Input */}
          <div className="space-y-2">
            <Label htmlFor="name">Link Name *</Label>
            <Input
              id="name"
              placeholder="e.g., Transport Manager"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="h-11"
            />
          </div>

          {/* URL Input */}
          <div className="space-y-2">
            <Label htmlFor="url">URL *</Label>
            <Input
              id="url"
              placeholder="e.g., /transport-manager or https://example.com"
              value={formData.url}
              onChange={(e) => {
                const url = e.target.value;
                const isExternal = url.startsWith('http://') || url.startsWith('https://');
                setFormData({ 
                  ...formData, 
                  url,
                  is_external: isExternal
                });
              }}
              className="h-11"
            />
            <p className="text-xs text-gray-500">
              Internal links start with / (e.g., /settings). External links start with http:// or https://
            </p>
          </div>

          {/* Icon Selector */}
          <div className="space-y-2">
            <Label>Select Icon</Label>
            <div className="grid grid-cols-4 sm:grid-cols-6 gap-3 max-h-60 overflow-y-auto p-3 border rounded-lg">
              {iconOptions.map((option) => {
                const IconComp = option.icon;
                return (
                  <button
                    key={option.name}
                    type="button"
                    onClick={() => setFormData({ ...formData, icon_type: option.name, icon_url: null })}
                    className={`p-3 rounded-lg border-2 transition-all hover:scale-105 ${
                      formData.icon_type === option.name && !formData.icon_url
                        ? 'border-orange-500 bg-orange-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    title={option.name}
                  >
                    <IconComp className="w-6 h-6 mx-auto" style={{ color: formData.icon_type === option.name && !formData.icon_url ? enconOrange : '#666' }} />
                  </button>
                );
              })}
            </div>
          </div>

          {/* Custom Icon Upload */}
          <div className="space-y-2">
            <Label>Or Upload Custom Icon</Label>
            <div className="flex items-center gap-3">
              <label className="flex-1">
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center cursor-pointer hover:border-orange-500 transition-all">
                  <Upload className="w-6 h-6 mx-auto mb-2 text-gray-400" />
                  <p className="text-sm text-gray-600">Click to upload image</p>
                  <p className="text-xs text-gray-400 mt-1">PNG, JPG up to 2MB</p>
                </div>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleIconUpload}
                  className="hidden"
                  disabled={uploadingIcon}
                />
              </label>
            </div>
            {uploadingIcon && <p className="text-sm text-gray-500">Uploading...</p>}
          </div>

          {/* Group Selector */}
          <div className="space-y-2">
            <Label htmlFor="group">Group (Optional)</Label>
            <select
              id="group"
              value={formData.group_id || ''}
              onChange={(e) => setFormData({ ...formData, group_id: e.target.value || null })}
              className="w-full h-11 px-3 border border-gray-300 rounded-lg"
            >
              <option value="">No Group (End of list)</option>
              {Array.isArray(groups) && groups.map((group) => (
                <option key={group.id} value={group.id}>
                  {group.name}
                </option>
              ))}
            </select>
          </div>

          {/* Global Checkbox (Super Admin Only) */}
          {user?.role === 'super_admin' && (
            <>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_global"
                  checked={formData.is_global}
                  onChange={(e) => setFormData({ ...formData, is_global: e.target.checked })}
                  className="w-4 h-4"
                />
                <Label htmlFor="is_global" className="cursor-pointer">
                  Make this link visible to all users (Global Link)
                </Label>
              </div>
              
              {/* External Link: Allow everyone to see checkbox */}
              {formData.is_external && (
                <div className="flex items-center gap-2 ml-6">
                  <input
                    type="checkbox"
                    id="visible_to_all"
                    checked={formData.visible_to_all}
                    onChange={(e) => setFormData({ ...formData, visible_to_all: e.target.checked })}
                    className="w-4 h-4"
                  />
                  <Label htmlFor="visible_to_all" className="cursor-pointer text-sm text-gray-600">
                    Allow everyone to see this external link (ignores page permissions)
                  </Label>
                </div>
              )}
            </>
          )}

          {/* Action Buttons */}
          <div className="flex items-center gap-3 pt-4">
            <Button
              onClick={onClose}
              variant="outline"
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              className="flex-1"
              style={{ backgroundColor: enconOrange, color: 'white' }}
            >
              {editLink ? 'Update' : 'Add'} Link
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DashboardLinkModal;
